/* eslint-disable max-lines */
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  CircularProgress,
  Paper,
  TableContainer,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Collapse,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Alert,
  Pagination,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Edit,
  Delete,
  Visibility,
  Refresh,
  ExpandMore,
  ExpandLess,
  FilterList,
  Clear,
  Close,
  Save,
  Business,
  Person,
  Assignment,
  Event,
  Folder,
  Source,
  Info as InfoIcon
} from '@mui/icons-material';
import api from '../services/api';
import useApiErrorHandler from '../hooks/useApiErrorHandler';
import { useEntityContext } from './EntityContextSwitch';

// eslint-disable-next-line max-lines-per-function
function ExistingEntities({ entities: propEntities }) {
  const { currentEntity } = useEntityContext();

  // Load globalSearch from localStorage (default: false)
  const [globalSearch, setGlobalSearch] = useState(() => {
    const saved = localStorage.getItem('entitySearchGlobalMode');
    return saved === 'true';
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [entities, setEntities] = useState(Array.isArray(propEntities) ? propEntities : []);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [editFormData, setEditFormData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Delete dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Filter states
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tagsFilter, setTagsFilter] = useState([]);
  const [dateFilter, setDateFilter] = useState('all'); // all, today, week, month, year

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [_totalPages, setTotalPages] = useState(1);
  const [totalEntities, setTotalEntities] = useState(0);

  // Load pageSize from localStorage with default of 50
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem('entitySearchPageSize');
    return saved ? parseInt(saved, 10) : 50;
  });

  // Persist pageSize to localStorage on change
  useEffect(() => {
    localStorage.setItem('entitySearchPageSize', pageSize.toString());
  }, [pageSize]);

  const {
    loading,
    error,
    executeApiCall,
    clearError
  } = useApiErrorHandler();

  // Get all unique tags from entities
  const availableTags = [...new Set(Array.isArray(entities) ? entities.flatMap(entity => entity.tags || []) : [])];

  // Helper function to format Firestore timestamps or date strings
  const formatTimestamp = (timestamp, includeTime = true) => {
    if (!timestamp) return 'N/A';

    let date;

    // Handle Firestore Timestamp object with _seconds
    if (timestamp._seconds !== undefined) {
      date = new Date(timestamp._seconds * 1000);
    }
    // Handle Firestore Timestamp object with seconds (no underscore)
    else if (timestamp.seconds !== undefined) {
      date = new Date(timestamp.seconds * 1000);
    }
    // Handle ISO string or regular date string
    else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    }
    // Handle Date object
    else if (timestamp instanceof Date) {
      date = timestamp;
    }
    // Handle numeric timestamp
    else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    }
    else {
      return 'N/A';
    }

    // Check if date is valid
    if (isNaN(date.getTime())) return 'N/A';

    return includeTime ? date.toLocaleString() : date.toLocaleDateString();
  };

  // Helper to format score values with consistent display
  const formatScore = (value) => {
    if (value === undefined || value === null) return null;
    // Handle numeric values - show as number with up to 2 decimal places
    if (typeof value === 'number') {
      return Number.isInteger(value) ? value.toString() : value.toFixed(2);
    }
    return value.toString();
  };

  // Helper to convert snake_case or camelCase field names to readable labels
  const formatFieldLabel = (fieldName) => {
    return fieldName
      .replace(/_/g, ' ')                    // Convert snake_case
      .replace(/([A-Z])/g, ' $1')            // Convert camelCase
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim();
  };

  // Persist globalSearch to localStorage on change
  useEffect(() => {
    localStorage.setItem('entitySearchGlobalMode', globalSearch.toString());
  }, [globalSearch]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Reset to page 1 when search changes
      if (searchTerm !== debouncedSearchTerm) {
        setCurrentPage(1);
      }
    }, 300);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [typeFilter, statusFilter]);

  // Handler for page size changes
  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  // Entity types and statuses
  const entityTypes = ['ORGANIZATION', 'PERSON', 'PROJECT', 'PROGRAM', 'EVENT', 'RESOURCE'];
  const entityStatuses = ['ACTIVE', 'INACTIVE', 'PENDING', 'ARCHIVED'];

  // Only fetch entities if they weren't provided as props
  useEffect(() => {
    if (propEntities && propEntities.length > 0) {
      setEntities(propEntities);
    } else if (!propEntities) {
      // Define fetchEntities inside useEffect to avoid dependency issues
      const fetchEntities = async () => {
        try {
          const params = {
            page: currentPage,
            limit: pageSize,
            type: typeFilter || undefined,
            status: statusFilter || undefined,
            search: debouncedSearchTerm || undefined,
            entityId: globalSearch ? undefined : (currentEntity?.id || undefined),
            globalSearch: globalSearch || undefined
          };

          const data = await executeApiCall(
            () => api.getEntities(params),
            {
              operationName: 'Fetch Entities',
                          }
          );

          // Handle both old format (array) and new format (object with pagination)
          if (Array.isArray(data)) {
            setEntities(data);
            setTotalPages(1);
            setTotalEntities(data.length);
          } else if (data && data.entities) {
            setEntities(data.entities);
            setTotalPages(data.pagination?.totalPages || 1);
            setTotalEntities(data.pagination?.total || data.entities.length);
          } else {
            setEntities([]);
          }
        } catch (err) {
          console.error('Error fetching entities:', err);
          setEntities([]);
        }
      };

      fetchEntities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propEntities, currentPage, pageSize, typeFilter, statusFilter, debouncedSearchTerm, currentEntity?.id, globalSearch]);

  // Since we're doing server-side filtering, we only apply client-side filters
  // that aren't yet supported by the server (tags and date filters)
  const filteredEntities = Array.isArray(entities) ? entities.filter(entity => {
    // Tags filter (not yet server-side)
    const matchesTags = tagsFilter.length === 0 ||
      tagsFilter.every(filterTag => entity.tags?.includes(filterTag));

    // Date filter (not yet server-side)
    let matchesDate = true;
    if (dateFilter !== 'all' && entity.created_at) {
      // Handle Firestore Timestamp format
      let entityDate;
      const ts = entity.created_at;
      if (ts._seconds !== undefined) {
        entityDate = new Date(ts._seconds * 1000);
      } else if (ts.seconds !== undefined) {
        entityDate = new Date(ts.seconds * 1000);
      } else {
        entityDate = new Date(ts);
      }
      const now = new Date();

      // Skip if invalid date
      if (isNaN(entityDate.getTime())) {
        matchesDate = true;
      } else {
        switch (dateFilter) {
          case 'today':
            matchesDate = entityDate.toDateString() === now.toDateString();
            break;
          case 'week':
            {
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            matchesDate = entityDate >= weekAgo;
            break;
            }
          case 'month':
            {
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            matchesDate = entityDate >= monthAgo;
            break;
            }
          case 'year':
            {
            const yearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
            matchesDate = entityDate >= yearAgo;
            break;
            }
          default:
            matchesDate = true;
        }
      }
    }

    return matchesTags && matchesDate;
  }) : [];

  // Count active filters
  const activeFilterCount =
    (typeFilter ? 1 : 0) +
    (statusFilter ? 1 : 0) +
    (tagsFilter.length > 0 ? 1 : 0) +
    (dateFilter !== 'all' ? 1 : 0);

  // Clear all filters
  const clearFilters = () => {
    setTypeFilter('');
    setStatusFilter('');
    setTagsFilter([]);
    setDateFilter('all');
  };

  // Helper function to extract type-specific fields
  const extractTypeSpecificFields = (entity) => {
    const fields = {};
    const entityType = (entity.type || '').toUpperCase();

    switch (entityType) {
      case 'PERSON':
        {
        {
        {
        {
        fields.firstName = entity.firstName || '';
        fields.lastName = entity.lastName || '';
        fields.role = entity.role || '';
        fields.organization = entity.organization || '';
        fields.bio = entity.bio || '';
        // Handle skills as either array or comma-separated string
        fields.skills = Array.isArray(entity.skills)
          ? entity.skills
          : (entity.skills ? String(entity.skills).split(',').map(s => s.trim()).filter(Boolean) : []);
        fields.linkedinUrl = entity.linkedinUrl || '';

        // Faana Leadership Quality Scores
        const lqs = entity.faana?.leadership_quality_scores || {};
        fields.courage_score = lqs.courage_score ?? '';
        fields.vulnerability_score = lqs.vulnerability_score ?? '';
        fields.purpose_driven_score = lqs.purpose_driven_score ?? '';
        fields.learning_agility_score = lqs.learning_agility_score ?? '';
        fields.determination_score = lqs.determination_score ?? '';
        fields.self_awareness_score = lqs.self_awareness_score ?? '';
        fields.adaptability_score = lqs.adaptability_score ?? '';
        fields.cross_cultural_score = lqs.cross_cultural_score ?? '';

        // Faana Framework Scores
        const fs = entity.faana?.framework_scores || {};
        fields.holistic_health_score = fs.holistic_health_score ?? '';
        fields.financial_fitness_score = fs.financial_fitness_score ?? '';
        fields.community_engagement_score = fs.community_engagement_score ?? '';
        fields.leadership_strength_score = fs.leadership_strength_score ?? '';

        // Faana Growth Metrics
        const gm = entity.faana?.growth_metrics || {};
        fields.growth_trajectory = gm.growth_trajectory || '';
        fields.growth_velocity = gm.growth_velocity || '';
        fields.assessment_date = gm.assessment_date || '';
        fields.coach_name = gm.coach_name || '';

        // Faana Risk Assessment
        const ra = entity.faana?.risk_assessment || {};
        fields.growth_risk_index = ra.growth_risk_index ?? '';
        fields.risk_factors = Array.isArray(ra.risk_factors)
          ? ra.risk_factors.join(', ')
          : (ra.risk_factors || '');

        // Notes
        fields.notes = entity.notes || '';
        break;
        }
        }
        }
        }
      case 'PROJECT':
        fields.projectName = entity.projectName || '';
        fields.startDate = entity.startDate || '';
        fields.endDate = entity.endDate || '';
        fields.projectLead = entity.projectLead || '';
        fields.objectives = entity.objectives || '';
        fields.deliverables = entity.deliverables || [];
        fields.budget = entity.budget || '';
        break;
      case 'PROGRAM':
        fields.programType = entity.programType || '';
        fields.duration = entity.duration || '';
        fields.targetAudience = entity.targetAudience || '';
        fields.facilitators = entity.facilitators || [];
        fields.outcomes = entity.outcomes || [];
        fields.maxParticipants = entity.maxParticipants || '';
        break;
      case 'EVENT':
        fields.eventDate = entity.eventDate || '';
        fields.eventTime = entity.eventTime || '';
        fields.location = entity.location || '';
        fields.eventType = entity.eventType || '';
        fields.capacity = entity.capacity || '';
        fields.registrationUrl = entity.registrationUrl || '';
        fields.agenda = entity.agenda || '';
        break;
      case 'RESOURCE':
        fields.resourceType = entity.resourceType || '';
        fields.resourceUrl = entity.resourceUrl || '';
        fields.accessLevel = entity.accessLevel || 'PUBLIC';
        fields.owner = entity.owner || '';
        fields.version = entity.version || '';
        fields.lastUpdated = entity.lastUpdated || '';
        break;
    }

    return fields;
  };

  // Dialog handlers
  const handleView = async (entity) => {
    try {
      // Fetch full entity details
      const fullEntity = await executeApiCall(
        () => api.getEntityById(entity.id || entity.entityId),
        {
          operationName: 'Fetch Entity Details'
        }
      );
      setSelectedEntity(fullEntity || entity);
      setViewDialogOpen(true);
    } catch (err) {
      console.error('Error fetching entity details:', err);
      // Fall back to using the list data
      setSelectedEntity(entity);
      setViewDialogOpen(true);
    }
  };

  const handleEdit = async (entity) => {
    try {
      // Fetch full entity details
      const fullEntity = await executeApiCall(
        () => api.getEntityById(entity.id || entity.entityId),
        {
          operationName: 'Fetch Entity Details'
        }
      );
      const entityData = fullEntity || entity;
      setSelectedEntity(entityData);
      setEditFormData({
        name: entityData.name || '',
        type: (entityData.type || 'ORGANIZATION').toUpperCase(),
        status: (entityData.status || 'ACTIVE').toUpperCase(),
        description: entityData.description || '',
        tags: Array.isArray(entityData.tags) ? entityData.tags : (entityData.tags ? String(entityData.tags).split(',').map(s => s.trim()).filter(Boolean) : []),
        // Contact information
        email: entityData.email || '',
        phone: entityData.phone || '',
        website: entityData.website || '',
        address: entityData.address || '',
        // Metadata
        tier: entityData.tier || entityData.metadata?.tier || '',
        region: entityData.region || entityData.metadata?.region || '',
        // Type-specific fields
        ...extractTypeSpecificFields(entityData)
      });
      setSaveError(null);
      setEditDialogOpen(true);
    } catch (err) {
      console.error('Error fetching entity details:', err);
      // Fall back to using the list data
      setSelectedEntity(entity);
      setEditFormData({
        name: entity.name || '',
        type: (entity.type || 'ORGANIZATION').toUpperCase(),
        status: (entity.status || 'ACTIVE').toUpperCase(),
        description: entity.description || '',
        tags: Array.isArray(entity.tags) ? entity.tags : (entity.tags ? String(entity.tags).split(',').map(s => s.trim()).filter(Boolean) : []),
        // Contact information
        email: entity.email || '',
        phone: entity.phone || '',
        website: entity.website || '',
        address: entity.address || '',
        // Metadata
        tier: entity.tier || entity.metadata?.tier || '',
        region: entity.region || entity.metadata?.region || '',
        // Type-specific fields
        ...extractTypeSpecificFields(entity)
      });
      setSaveError(null);
      setEditDialogOpen(true);
    }
  };

  const handleDelete = (entity) => {
    setSelectedEntity(entity);
    setDeleteConfirmText('');
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedEntity) return;

    // Check if the user typed the correct confirmation
    if (deleteConfirmText !== selectedEntity.name) {
      setDeleteError('Confirmation text does not match. Please type the exact name.');
      return;
    }

    setDeleting(true);
    setDeleteError(null);

    try {
      await executeApiCall(
        () => api.deleteEntity(selectedEntity.id || selectedEntity.entityId),
        {
          operationName: 'Delete Entity',
                  }
      );

      // Remove the entity from local state
      setEntities(prevEntities =>
        prevEntities.filter(e => e.id !== selectedEntity.id && e.entityId !== selectedEntity.entityId)
      );

      setDeleteDialogOpen(false);
      setSelectedEntity(null);
      setDeleteConfirmText('');
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete entity');
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveEntity = async () => {
    setSaving(true);
    setSaveError(null);

    try {
      // Build the entity data with nested faana structure for PERSON type
      const entityData = { ...editFormData };

      if (editFormData.type === 'PERSON') {
        // Build faana nested structure
        const faana = {};

        // Leadership Quality Scores
        const leadershipScores = {};
        const leadershipFields = [
          'courage_score', 'vulnerability_score', 'purpose_driven_score',
          'learning_agility_score', 'determination_score', 'self_awareness_score',
          'adaptability_score', 'cross_cultural_score'
        ];
        leadershipFields.forEach(field => {
          if (editFormData[field] !== '' && editFormData[field] !== undefined) {
            leadershipScores[field] = parseFloat(editFormData[field]);
          }
          delete entityData[field];
        });
        if (Object.keys(leadershipScores).length > 0) {
          faana.leadership_quality_scores = leadershipScores;
        }

        // Framework Scores
        const frameworkScores = {};
        const frameworkFields = [
          'holistic_health_score', 'financial_fitness_score',
          'community_engagement_score', 'leadership_strength_score'
        ];
        frameworkFields.forEach(field => {
          if (editFormData[field] !== '' && editFormData[field] !== undefined) {
            frameworkScores[field] = parseFloat(editFormData[field]);
          }
          delete entityData[field];
        });
        if (Object.keys(frameworkScores).length > 0) {
          faana.framework_scores = frameworkScores;
        }

        // Growth Metrics
        const growthMetrics = {};
        if (editFormData.growth_trajectory) {
          growthMetrics.growth_trajectory = editFormData.growth_trajectory;
        }
        if (editFormData.growth_velocity) {
          growthMetrics.growth_velocity = editFormData.growth_velocity;
        }
        if (editFormData.assessment_date) {
          growthMetrics.assessment_date = editFormData.assessment_date;
        }
        if (editFormData.coach_name) {
          growthMetrics.coach_name = editFormData.coach_name;
        }
        delete entityData.growth_trajectory;
        delete entityData.growth_velocity;
        delete entityData.assessment_date;
        delete entityData.coach_name;
        if (Object.keys(growthMetrics).length > 0) {
          faana.growth_metrics = growthMetrics;
        }

        // Risk Assessment
        const riskAssessment = {};
        if (editFormData.growth_risk_index !== '' && editFormData.growth_risk_index !== undefined) {
          riskAssessment.growth_risk_index = parseFloat(editFormData.growth_risk_index);
        }
        if (editFormData.risk_factors) {
          // Convert comma-separated string to array
          riskAssessment.risk_factors = editFormData.risk_factors
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);
        }
        delete entityData.growth_risk_index;
        delete entityData.risk_factors;
        if (Object.keys(riskAssessment).length > 0) {
          faana.risk_assessment = riskAssessment;
        }

        // Add faana to entity data if it has content
        if (Object.keys(faana).length > 0) {
          entityData.faana = faana;
        }
      }

      await executeApiCall(
        () => api.updateEntity(selectedEntity.id || selectedEntity.entityId, entityData),
        {
          operationName: 'Update Entity',
        }
      );

      // Update the entity in the local state
      setEntities(prevEntities =>
        prevEntities.map(entity =>
          (entity.id === selectedEntity.id || entity.entityId === selectedEntity.entityId)
            ? { ...entity, ...entityData }
            : entity
        )
      );

      setEditDialogOpen(false);
    } catch (err) {
      setSaveError(err.message || 'Failed to update entity');
    } finally {
      setSaving(false);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'INACTIVE':
        return 'default';
      case 'PENDING':
        return 'warning';
      case 'ARCHIVED':
        return 'info';
      default:
        return 'default';
    }
  };

  // Get type color
  const getTypeColor = (type) => {
    switch (type) {
      case 'ORGANIZATION':
        return 'primary';
      case 'PERSON':
        return 'secondary';
      case 'PROJECT':
        return 'info';
      case 'PROGRAM':
        return 'success';
      case 'EVENT':
        return 'warning';
      case 'RESOURCE':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get type icon
  const getTypeIcon = (type) => {
    switch (type) {
      case 'ORGANIZATION':
        return <Business />;
      case 'PERSON':
        return <Person />;
      case 'PROJECT':
        return <Assignment />;
      case 'PROGRAM':
        return <Folder />;
      case 'EVENT':
        return <Event />;
      case 'RESOURCE':
        return <Source />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !entities.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" gutterBottom>
          Error loading entities: {error.message}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Refresh />}
          onClick={() => {
            clearError();
            // Define fetchEntities inline to avoid issues
            const refreshData = async () => {
              try {
                const params = {
                  page: currentPage,
                  limit: pageSize,
                  type: typeFilter || undefined,
                  status: statusFilter || undefined,
                  search: debouncedSearchTerm || undefined,
                  entityId: globalSearch ? undefined : (currentEntity?.id || undefined),
                  globalSearch: globalSearch || undefined
                };
                const data = await executeApiCall(
                  () => api.getEntities(params),
                  {
                    operationName: 'Retry Fetch Entities',
                                      }
                );
                setEntities(data);
              } catch (err) {
                console.error('Error retrying entity fetch:', err);
              }
            };
            refreshData().catch(err => console.error('Error during refresh:', err));
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <TextField
        fullWidth
        placeholder="Search entities by name, ID, type, or tags..."
        variant="outlined"
        margin="normal"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      {/* Global Search Toggle */}
      <Box sx={{ mt: 2, mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.primary">
                Global Search
              </Typography>
              <Tooltip title="Search across all entities you have access to, not just children of selected entity">
                <InfoIcon fontSize="small" color="action" />
              </Tooltip>
            </Box>
          }
        />
      </Box>
      {/* Filter section */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<FilterList />}
          endIcon={filtersOpen ? <ExpandLess /> : <ExpandMore />}
          onClick={() => setFiltersOpen(!filtersOpen)}
          sx={{ mr: 1 }}
        >
          <Badge badgeContent={activeFilterCount} color="primary">
            Filters
          </Badge>
        </Button>

        {activeFilterCount > 0 && (
          <Button
            variant="text"
            startIcon={<Clear />}
            onClick={clearFilters}
            size="small"
          >
            Clear Filters
          </Button>
        )}
      </Box>
      <Collapse in={filtersOpen}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
              {/* Type filter */}
              <FormControl fullWidth size="small">
                <InputLabel id="type-filter-label">Type</InputLabel>
                <Select
                  labelId="type-filter-label"
                  value={typeFilter}
                  label="Type"
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {entityTypes.map(type => (
                    <MenuItem key={type} value={type}>
                      <Chip
                        label={type}
                        size="small"
                        color={getTypeColor(type)}
                        sx={{ mr: 1 }}
                      />
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Status filter */}
              <FormControl fullWidth size="small">
                <InputLabel id="status-filter-label">Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {entityStatuses.map(status => (
                    <MenuItem key={status} value={status}>
                      <Chip
                        label={status}
                        size="small"
                        color={getStatusColor(status)}
                        sx={{ mr: 1 }}
                      />
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Tags filter */}
              <Autocomplete
                multiple
                size="small"
                options={availableTags}
                value={tagsFilter}
                onChange={(event, newValue) => setTagsFilter(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Tags" placeholder="Select tags" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
                  ))
                }
              />

              {/* Date filter */}
              <FormControl fullWidth size="small">
                <InputLabel id="date-filter-label">Created</InputLabel>
                <Select
                  labelId="date-filter-label"
                  value={dateFilter}
                  label="Created"
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="week">Last 7 Days</MenuItem>
                  <MenuItem value="month">Last 30 Days</MenuItem>
                  <MenuItem value="year">Last Year</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Active filters display */}
            {activeFilterCount > 0 && (
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="body2" sx={{ mr: 1 }}>Active filters:</Typography>
                {typeFilter && (
                  <Chip
                    label={`Type: ${typeFilter}`}
                    size="small"
                    onDelete={() => setTypeFilter('')}
                    color="primary"
                    variant="outlined"
                  />
                )}
                {statusFilter && (
                  <Chip
                    label={`Status: ${statusFilter}`}
                    size="small"
                    onDelete={() => setStatusFilter('')}
                    color="primary"
                    variant="outlined"
                  />
                )}
                {tagsFilter.map(tag => (
                  <Chip
                    key={tag}
                    label={`Tag: ${tag}`}
                    size="small"
                    onDelete={() => setTagsFilter(tagsFilter.filter(t => t !== tag))}
                    color="primary"
                    variant="outlined"
                  />
                ))}
                {dateFilter !== 'all' && (
                  <Chip
                    label={`Created: ${dateFilter}`}
                    size="small"
                    onDelete={() => setDateFilter('all')}
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Collapse>
      {/* Results summary */}
      <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredEntities.length} of {entities.length} entities
          {(searchTerm || activeFilterCount > 0) && ' (filtered)'}
        </Typography>
      </Box>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredEntities.length > 0 ? (
              filteredEntities.map(entity => (
                <TableRow key={entity.id || entity.entityId}>
                  <TableCell>{entity.id || entity.entityId}</TableCell>
                  <TableCell>{entity.name || 'No name'}</TableCell>
                  <TableCell>
                    <Chip
                      label={entity.type || 'Unknown'}
                      size="small"
                      color={getTypeColor(entity.type)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={entity.status || 'Unknown'}
                      size="small"
                      color={getStatusColor(entity.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {entity.tags && entity.tags.length > 0 ? (
                        entity.tags.map((tag, index) => (
                          <Chip key={index} label={tag} size="small" variant="outlined" />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {formatTimestamp(entity.created_at, false)}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton size="small" color="primary" onClick={() => handleView(entity)}>
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Entity">
                      <IconButton size="small" color="primary" onClick={() => handleEdit(entity)}>
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Entity">
                      <IconButton size="small" color="error" onClick={() => handleDelete(entity)}>
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body1" sx={{ py: 2 }}>
                    {searchTerm ? 'No matching entities found.' : 'No entities available.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Pagination */}
      {!propEntities && totalEntities > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {Math.min((currentPage - 1) * pageSize + 1, totalEntities)}-{Math.min(currentPage * pageSize, totalEntities)} of {totalEntities} results
          </Typography>

          <Pagination
            count={Math.max(1, Math.ceil(totalEntities / pageSize))}
            page={currentPage}
            onChange={(e, page) => setCurrentPage(page)}
            color="primary"
            showFirstButton
            showLastButton
            size="large"
          />

          <Select
            value={pageSize}
            onChange={handlePageSizeChange}
            size="small"
            sx={{ minWidth: 120 }}
          >
            <MenuItem value={25}>25 per page</MenuItem>
            <MenuItem value={50}>50 per page</MenuItem>
            <MenuItem value={100}>100 per page</MenuItem>
            <MenuItem value={200}>200 per page</MenuItem>
          </Select>
        </Box>
      )}
      {/* View Entity Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {selectedEntity && getTypeIcon(selectedEntity.type)}
              <Typography variant="h6">View Entity Details</Typography>
            </Box>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setViewDialogOpen(false)}
              aria-label="close"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedEntity && (
            <Grid container spacing={3}>
              <Grid size={12}>
                <Typography variant="subtitle2" color="text.secondary">Entity ID</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                  {selectedEntity.id || selectedEntity.entityId}
                </Typography>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{selectedEntity.name || 'No name'}</Typography>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                <Chip
                  icon={getTypeIcon(selectedEntity.type)}
                  label={selectedEntity.type || 'Unknown'}
                  color={getTypeColor(selectedEntity.type)}
                  sx={{ mt: 0.5 }}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip
                  label={selectedEntity.status || 'Unknown'}
                  color={getStatusColor(selectedEntity.status)}
                  sx={{ mt: 0.5 }}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <Typography variant="subtitle2" color="text.secondary">Created At</Typography>
                <Typography variant="body1">
                  {formatTimestamp(selectedEntity.created_at)}
                </Typography>
              </Grid>

              {selectedEntity.updated_at && (
                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <Typography variant="subtitle2" color="text.secondary">Updated At</Typography>
                  <Typography variant="body1">
                    {formatTimestamp(selectedEntity.updated_at)}
                  </Typography>
                </Grid>
              )}

              {selectedEntity.created_by && (
                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <Typography variant="subtitle2" color="text.secondary">Created By</Typography>
                  <Typography variant="body1">
                    {selectedEntity.created_by}
                  </Typography>
                </Grid>
              )}

              {selectedEntity.updated_by && (
                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <Typography variant="subtitle2" color="text.secondary">Updated By</Typography>
                  <Typography variant="body1">
                    {selectedEntity.updated_by}
                  </Typography>
                </Grid>
              )}

              {selectedEntity.tier && (
                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <Typography variant="subtitle2" color="text.secondary">Tier</Typography>
                  <Typography variant="body1">
                    {selectedEntity.tier}
                  </Typography>
                </Grid>
              )}

              {selectedEntity.region && (
                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <Typography variant="subtitle2" color="text.secondary">Region</Typography>
                  <Typography variant="body1">
                    {selectedEntity.region}
                  </Typography>
                </Grid>
              )}

              {selectedEntity.description && (
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedEntity.description}
                  </Typography>
                </Grid>
              )}

              {/* Contact Information */}
              {(selectedEntity.email || selectedEntity.phone || selectedEntity.website || selectedEntity.address) && (
                <>
                  <Grid size={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h6" sx={{ mb: 2 }}>Contact Information</Typography>
                  </Grid>

                  {selectedEntity.email && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                      <Typography variant="body1">
                        {selectedEntity.email}
                      </Typography>
                    </Grid>
                  )}

                  {selectedEntity.phone && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                      <Typography variant="body1">
                        {selectedEntity.phone}
                      </Typography>
                    </Grid>
                  )}

                  {selectedEntity.website && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography variant="subtitle2" color="text.secondary">Website</Typography>
                      <Typography variant="body1">
                        <a href={selectedEntity.website} target="_blank" rel="noopener noreferrer">
                          {selectedEntity.website}
                        </a>
                      </Typography>
                    </Grid>
                  )}

                  {selectedEntity.address && (
                    <Grid size={12}>
                      <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                      <Typography variant="body1">
                        {selectedEntity.address}
                      </Typography>
                    </Grid>
                  )}
                </>
              )}

              {/* Type-specific fields */}
              {selectedEntity.type === 'PERSON' && (selectedEntity.firstName || selectedEntity.lastName || selectedEntity.role || selectedEntity.organization || selectedEntity.bio || selectedEntity.linkedinUrl) && (
                <>
                  <Grid size={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h6" sx={{ mb: 2 }}>Person Details</Typography>
                  </Grid>

                  {(selectedEntity.firstName || selectedEntity.lastName) && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography variant="subtitle2" color="text.secondary">Full Name</Typography>
                      <Typography variant="body1">
                        {[selectedEntity.firstName, selectedEntity.lastName].filter(Boolean).join(' ')}
                      </Typography>
                    </Grid>
                  )}

                  {selectedEntity.role && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography variant="subtitle2" color="text.secondary">Role</Typography>
                      <Typography variant="body1">
                        {selectedEntity.role}
                      </Typography>
                    </Grid>
                  )}

                  {selectedEntity.organization && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography variant="subtitle2" color="text.secondary">Organization</Typography>
                      <Typography variant="body1">
                        {selectedEntity.organization}
                      </Typography>
                    </Grid>
                  )}

                  {selectedEntity.linkedinUrl && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography variant="subtitle2" color="text.secondary">LinkedIn</Typography>
                      <Typography variant="body1">
                        <a href={selectedEntity.linkedinUrl} target="_blank" rel="noopener noreferrer">
                          View Profile
                        </a>
                      </Typography>
                    </Grid>
                  )}

                  {selectedEntity.bio && (
                    <Grid size={12}>
                      <Typography variant="subtitle2" color="text.secondary">Bio</Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedEntity.bio}
                      </Typography>
                    </Grid>
                  )}

                  {selectedEntity.skills && selectedEntity.skills.length > 0 && (
                    <Grid size={12}>
                      <Typography variant="subtitle2" color="text.secondary">Skills</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                        {selectedEntity.skills.map((skill, index) => (
                          <Chip key={index} label={skill} size="small" color="primary" variant="outlined" />
                        ))}
                      </Box>
                    </Grid>
                  )}
                </>
              )}

              {/* Project-specific fields */}
              {selectedEntity.type === 'PROJECT' && (selectedEntity.projectName || selectedEntity.startDate || selectedEntity.endDate || selectedEntity.projectLead || selectedEntity.objectives || selectedEntity.budget) && (
                <>
                  <Grid size={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h6" sx={{ mb: 2 }}>Project Details</Typography>
                  </Grid>

                  {selectedEntity.startDate && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography variant="subtitle2" color="text.secondary">Start Date</Typography>
                      <Typography variant="body1">
                        {new Date(selectedEntity.startDate).toLocaleDateString()}
                      </Typography>
                    </Grid>
                  )}

                  {selectedEntity.endDate && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography variant="subtitle2" color="text.secondary">End Date</Typography>
                      <Typography variant="body1">
                        {new Date(selectedEntity.endDate).toLocaleDateString()}
                      </Typography>
                    </Grid>
                  )}

                  {selectedEntity.projectLead && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography variant="subtitle2" color="text.secondary">Project Lead</Typography>
                      <Typography variant="body1">
                        {selectedEntity.projectLead}
                      </Typography>
                    </Grid>
                  )}

                  {selectedEntity.budget && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography variant="subtitle2" color="text.secondary">Budget</Typography>
                      <Typography variant="body1">
                        {selectedEntity.budget}
                      </Typography>
                    </Grid>
                  )}

                  {selectedEntity.objectives && (
                    <Grid size={12}>
                      <Typography variant="subtitle2" color="text.secondary">Objectives</Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedEntity.objectives}
                      </Typography>
                    </Grid>
                  )}

                  {selectedEntity.deliverables && selectedEntity.deliverables.length > 0 && (
                    <Grid size={12}>
                      <Typography variant="subtitle2" color="text.secondary">Deliverables</Typography>
                      <Box sx={{ mt: 1 }}>
                        {selectedEntity.deliverables.map((deliverable, index) => (
                          <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                            • {deliverable}
                          </Typography>
                        ))}
                      </Box>
                    </Grid>
                  )}
                </>
              )}

              {/* Event-specific fields */}
              {selectedEntity.type === 'EVENT' && (selectedEntity.eventDate || selectedEntity.eventTime || selectedEntity.location || selectedEntity.eventType || selectedEntity.capacity || selectedEntity.registrationUrl || selectedEntity.agenda) && (
                <>
                  <Grid size={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h6" sx={{ mb: 2 }}>Event Details</Typography>
                  </Grid>

                  {selectedEntity.eventDate && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography variant="subtitle2" color="text.secondary">Event Date</Typography>
                      <Typography variant="body1">
                        {new Date(selectedEntity.eventDate).toLocaleDateString()}
                      </Typography>
                    </Grid>
                  )}

                  {selectedEntity.eventTime && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography variant="subtitle2" color="text.secondary">Event Time</Typography>
                      <Typography variant="body1">
                        {selectedEntity.eventTime}
                      </Typography>
                    </Grid>
                  )}

                  {selectedEntity.location && (
                    <Grid size={12}>
                      <Typography variant="subtitle2" color="text.secondary">Location</Typography>
                      <Typography variant="body1">
                        {selectedEntity.location}
                      </Typography>
                    </Grid>
                  )}

                  {selectedEntity.eventType && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography variant="subtitle2" color="text.secondary">Event Type</Typography>
                      <Typography variant="body1">
                        {selectedEntity.eventType}
                      </Typography>
                    </Grid>
                  )}

                  {selectedEntity.capacity && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography variant="subtitle2" color="text.secondary">Capacity</Typography>
                      <Typography variant="body1">
                        {selectedEntity.capacity}
                      </Typography>
                    </Grid>
                  )}

                  {selectedEntity.registrationUrl && (
                    <Grid size={12}>
                      <Typography variant="subtitle2" color="text.secondary">Registration</Typography>
                      <Typography variant="body1">
                        <a href={selectedEntity.registrationUrl} target="_blank" rel="noopener noreferrer">
                          Register Here
                        </a>
                      </Typography>
                    </Grid>
                  )}

                  {selectedEntity.agenda && (
                    <Grid size={12}>
                      <Typography variant="subtitle2" color="text.secondary">Agenda</Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedEntity.agenda}
                      </Typography>
                    </Grid>
                  )}
                </>
              )}

              {/* Metadata */}
              {selectedEntity.metadata && Object.keys(selectedEntity.metadata).length > 0 && (
                <>
                  <Grid size={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h6" sx={{ mb: 2 }}>Additional Metadata</Typography>
                  </Grid>

                  {Object.entries(selectedEntity.metadata).map(([key, value]) => (
                    <Grid
                      key={key}
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                      </Typography>
                      <Typography variant="body1">
                        {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                      </Typography>
                    </Grid>
                  ))}
                </>
              )}

              {/* Tags */}
              <Grid size={12}>
                <Typography variant="subtitle2" color="text.secondary">Tags</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                  {selectedEntity.tags && selectedEntity.tags.length > 0 ? (
                    selectedEntity.tags.map((tag, index) => (
                      <Chip key={index} label={tag} size="small" variant="outlined" />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">No tags</Typography>
                  )}
                </Box>
              </Grid>

              {/* Faana Leadership Quality Scores */}
              {selectedEntity.faana?.leadership_quality_scores && Object.keys(selectedEntity.faana.leadership_quality_scores).length > 0 && (
                <>
                  <Grid size={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h6" sx={{ mb: 2 }}>Leadership Quality Scores</Typography>
                  </Grid>

                  {Object.entries(selectedEntity.faana.leadership_quality_scores).map(([key, value]) => {
                    const formattedValue = formatScore(value);
                    if (formattedValue === null) return null;
                    return (
                      <Grid
                        key={key}
                        size={{
                          xs: 6,
                          sm: 4,
                          md: 3
                        }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {formatFieldLabel(key)}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {formattedValue}
                        </Typography>
                      </Grid>
                    );
                  })}
                </>
              )}

              {/* Faana Framework Scores */}
              {selectedEntity.faana?.framework_scores && Object.keys(selectedEntity.faana.framework_scores).length > 0 && (
                <>
                  <Grid size={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h6" sx={{ mb: 2 }}>Framework Scores</Typography>
                  </Grid>

                  {Object.entries(selectedEntity.faana.framework_scores).map(([key, value]) => {
                    const formattedValue = formatScore(value);
                    if (formattedValue === null) return null;
                    return (
                      <Grid
                        key={key}
                        size={{
                          xs: 6,
                          sm: 4,
                          md: 3
                        }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {formatFieldLabel(key)}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {formattedValue}
                        </Typography>
                      </Grid>
                    );
                  })}
                </>
              )}

              {/* Faana Growth Metrics */}
              {selectedEntity.faana?.growth_metrics && Object.keys(selectedEntity.faana.growth_metrics).length > 0 && (
                <>
                  <Grid size={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h6" sx={{ mb: 2 }}>Growth Metrics</Typography>
                  </Grid>

                  {selectedEntity.faana.growth_metrics.growth_trajectory && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography variant="subtitle2" color="text.secondary">Growth Trajectory</Typography>
                      <Chip
                        label={selectedEntity.faana.growth_metrics.growth_trajectory}
                        color={
                          selectedEntity.faana.growth_metrics.growth_trajectory === 'Master' ? 'success' :
                          selectedEntity.faana.growth_metrics.growth_trajectory === 'Advanced' ? 'primary' :
                          selectedEntity.faana.growth_metrics.growth_trajectory === 'Developing' ? 'warning' :
                          'default'
                        }
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                  )}

                  {selectedEntity.faana.growth_metrics.growth_velocity && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography variant="subtitle2" color="text.secondary">Growth Velocity</Typography>
                      <Chip
                        label={selectedEntity.faana.growth_metrics.growth_velocity}
                        color={
                          selectedEntity.faana.growth_metrics.growth_velocity === 'Rapid' ? 'success' :
                          selectedEntity.faana.growth_metrics.growth_velocity === 'Moderate' ? 'primary' :
                          selectedEntity.faana.growth_metrics.growth_velocity === 'Slow' ? 'warning' :
                          'default'
                        }
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                  )}

                  {selectedEntity.faana.growth_metrics.assessment_date && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography variant="subtitle2" color="text.secondary">Assessment Date</Typography>
                      <Typography variant="body1">
                        {formatTimestamp(selectedEntity.faana.growth_metrics.assessment_date, false)}
                      </Typography>
                    </Grid>
                  )}

                  {selectedEntity.faana.growth_metrics.coach_name && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography variant="subtitle2" color="text.secondary">Coach Name</Typography>
                      <Typography variant="body1">
                        {selectedEntity.faana.growth_metrics.coach_name}
                      </Typography>
                    </Grid>
                  )}
                </>
              )}

              {/* Faana Risk Assessment */}
              {selectedEntity.faana?.risk_assessment && Object.keys(selectedEntity.faana.risk_assessment).length > 0 && (
                <>
                  <Grid size={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h6" sx={{ mb: 2 }}>Risk Assessment</Typography>
                  </Grid>

                  {selectedEntity.faana.risk_assessment.growth_risk_index !== undefined && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography variant="subtitle2" color="text.secondary">Growth Risk Index</Typography>
                      <Chip
                        label={formatScore(selectedEntity.faana.risk_assessment.growth_risk_index)}
                        color={
                          selectedEntity.faana.risk_assessment.growth_risk_index <= 1.5 ? 'success' :
                          selectedEntity.faana.risk_assessment.growth_risk_index <= 2.5 ? 'warning' :
                          'error'
                        }
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                  )}

                  {selectedEntity.faana.risk_assessment.risk_factors && (
                    <Grid size={12}>
                      <Typography variant="subtitle2" color="text.secondary">Risk Factors</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                        {(Array.isArray(selectedEntity.faana.risk_assessment.risk_factors)
                          ? selectedEntity.faana.risk_assessment.risk_factors
                          : String(selectedEntity.faana.risk_assessment.risk_factors).split(',').map(s => s.trim()).filter(Boolean)
                        ).map((factor, index) => (
                          <Chip
                            key={index}
                            label={formatFieldLabel(factor)}
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Grid>
                  )}

                  {selectedEntity.faana.risk_assessment.mitigation_plan_id && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography variant="subtitle2" color="text.secondary">Mitigation Plan ID</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {selectedEntity.faana.risk_assessment.mitigation_plan_id}
                      </Typography>
                    </Grid>
                  )}
                </>
              )}

              {/* Notes */}
              {selectedEntity.notes && (
                <>
                  <Grid size={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h6" sx={{ mb: 2 }}>Notes</Typography>
                  </Grid>

                  <Grid size={12}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                      {selectedEntity.notes}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => {
              setViewDialogOpen(false);
              handleEdit(selectedEntity);
            }}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>
      {/* Edit Entity Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !saving && setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {selectedEntity && getTypeIcon(editFormData?.type)}
              <Typography variant="h6">Edit Entity</Typography>
            </Box>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setEditDialogOpen(false)}
              aria-label="close"
              disabled={saving}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          )}

          {editFormData && (
            <Grid container spacing={3}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Name"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  required
                  disabled={saving}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={editFormData.type}
                    label="Type"
                    onChange={(e) => setEditFormData({ ...editFormData, type: e.target.value })}
                    disabled={saving}
                  >
                    {entityTypes.map(type => (
                      <MenuItem key={type} value={type}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getTypeIcon(type)}
                          {type}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editFormData.status}
                    label="Status"
                    onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                    disabled={saving}
                  >
                    {entityStatuses.map(status => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  multiline
                  rows={3}
                  disabled={saving}
                />
              </Grid>

              <Grid size={12}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={availableTags}
                  value={editFormData.tags || []}
                  onChange={(event, newValue) => setEditFormData({ ...editFormData, tags: newValue || [] })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tags"
                      placeholder="Add tags..."
                      helperText="Press Enter to add new tags"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    (value || []).map((option, index) => (
                      <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
                    ))
                  }
                  disabled={saving}
                />
              </Grid>

              {/* Contact Information Section */}
              <Grid size={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 2 }}>Contact Information</Typography>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={editFormData.email || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  disabled={saving}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={editFormData.phone || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                  disabled={saving}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  fullWidth
                  label="Website"
                  value={editFormData.website || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, website: e.target.value })}
                  disabled={saving}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  fullWidth
                  label="Address"
                  value={editFormData.address || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                  disabled={saving}
                />
              </Grid>

              {/* Additional Metadata */}
              <Grid size={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 2 }}>Additional Information</Typography>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  fullWidth
                  label="Tier"
                  value={editFormData.tier || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, tier: e.target.value })}
                  disabled={saving}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  fullWidth
                  label="Region"
                  value={editFormData.region || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, region: e.target.value })}
                  disabled={saving}
                />
              </Grid>

              {/* Type-specific fields */}
              {editFormData.type === 'PERSON' && (
                <>
                  <Grid size={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>Person Details</Typography>
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={editFormData.firstName || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, firstName: e.target.value })}
                      disabled={saving}
                    />
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={editFormData.lastName || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, lastName: e.target.value })}
                      disabled={saving}
                    />
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <TextField
                      fullWidth
                      label="Role"
                      value={editFormData.role || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                      disabled={saving}
                    />
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <TextField
                      fullWidth
                      label="Organization"
                      value={editFormData.organization || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, organization: e.target.value })}
                      disabled={saving}
                    />
                  </Grid>

                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Bio"
                      value={editFormData.bio || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, bio: e.target.value })}
                      multiline
                      rows={3}
                      disabled={saving}
                    />
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <TextField
                      fullWidth
                      label="LinkedIn URL"
                      value={editFormData.linkedinUrl || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, linkedinUrl: e.target.value })}
                      disabled={saving}
                    />
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[]}
                      value={editFormData.skills || []}
                      onChange={(event, newValue) => setEditFormData({ ...editFormData, skills: newValue || [] })}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Skills"
                          placeholder="Add skills..."
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        (value || []).map((option, index) => (
                          <Chip variant="outlined" label={option} size="small" color="primary" {...getTagProps({ index })} />
                        ))
                      }
                      disabled={saving}
                    />
                  </Grid>

                  {/* Faana Leadership Quality Scores */}
                  <Grid size={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>Leadership Quality Scores</Typography>
                  </Grid>

                  <Grid
                    size={{
                      xs: 6,
                      sm: 4,
                      md: 3
                    }}>
                    <TextField
                      fullWidth
                      label="Courage"
                      type="number"
                      inputProps={{ step: 0.1, min: 0, max: 5 }}
                      value={editFormData.courage_score}
                      onChange={(e) => setEditFormData({ ...editFormData, courage_score: e.target.value })}
                      disabled={saving}
                      size="small"
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 6,
                      sm: 4,
                      md: 3
                    }}>
                    <TextField
                      fullWidth
                      label="Vulnerability"
                      type="number"
                      inputProps={{ step: 0.1, min: 0, max: 5 }}
                      value={editFormData.vulnerability_score}
                      onChange={(e) => setEditFormData({ ...editFormData, vulnerability_score: e.target.value })}
                      disabled={saving}
                      size="small"
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 6,
                      sm: 4,
                      md: 3
                    }}>
                    <TextField
                      fullWidth
                      label="Purpose Driven"
                      type="number"
                      inputProps={{ step: 0.1, min: 0, max: 5 }}
                      value={editFormData.purpose_driven_score}
                      onChange={(e) => setEditFormData({ ...editFormData, purpose_driven_score: e.target.value })}
                      disabled={saving}
                      size="small"
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 6,
                      sm: 4,
                      md: 3
                    }}>
                    <TextField
                      fullWidth
                      label="Learning Agility"
                      type="number"
                      inputProps={{ step: 0.1, min: 0, max: 5 }}
                      value={editFormData.learning_agility_score}
                      onChange={(e) => setEditFormData({ ...editFormData, learning_agility_score: e.target.value })}
                      disabled={saving}
                      size="small"
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 6,
                      sm: 4,
                      md: 3
                    }}>
                    <TextField
                      fullWidth
                      label="Determination"
                      type="number"
                      inputProps={{ step: 0.1, min: 0, max: 5 }}
                      value={editFormData.determination_score}
                      onChange={(e) => setEditFormData({ ...editFormData, determination_score: e.target.value })}
                      disabled={saving}
                      size="small"
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 6,
                      sm: 4,
                      md: 3
                    }}>
                    <TextField
                      fullWidth
                      label="Self Awareness"
                      type="number"
                      inputProps={{ step: 0.1, min: 0, max: 5 }}
                      value={editFormData.self_awareness_score}
                      onChange={(e) => setEditFormData({ ...editFormData, self_awareness_score: e.target.value })}
                      disabled={saving}
                      size="small"
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 6,
                      sm: 4,
                      md: 3
                    }}>
                    <TextField
                      fullWidth
                      label="Adaptability"
                      type="number"
                      inputProps={{ step: 0.1, min: 0, max: 5 }}
                      value={editFormData.adaptability_score}
                      onChange={(e) => setEditFormData({ ...editFormData, adaptability_score: e.target.value })}
                      disabled={saving}
                      size="small"
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 6,
                      sm: 4,
                      md: 3
                    }}>
                    <TextField
                      fullWidth
                      label="Cross Cultural"
                      type="number"
                      inputProps={{ step: 0.1, min: 0, max: 5 }}
                      value={editFormData.cross_cultural_score}
                      onChange={(e) => setEditFormData({ ...editFormData, cross_cultural_score: e.target.value })}
                      disabled={saving}
                      size="small"
                    />
                  </Grid>

                  {/* Faana Framework Scores */}
                  <Grid size={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>Framework Scores</Typography>
                  </Grid>

                  <Grid
                    size={{
                      xs: 6,
                      sm: 4,
                      md: 3
                    }}>
                    <TextField
                      fullWidth
                      label="Holistic Health"
                      type="number"
                      inputProps={{ step: 1, min: 0, max: 100 }}
                      value={editFormData.holistic_health_score}
                      onChange={(e) => setEditFormData({ ...editFormData, holistic_health_score: e.target.value })}
                      disabled={saving}
                      size="small"
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 6,
                      sm: 4,
                      md: 3
                    }}>
                    <TextField
                      fullWidth
                      label="Financial Fitness"
                      type="number"
                      inputProps={{ step: 1, min: 0, max: 100 }}
                      value={editFormData.financial_fitness_score}
                      onChange={(e) => setEditFormData({ ...editFormData, financial_fitness_score: e.target.value })}
                      disabled={saving}
                      size="small"
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 6,
                      sm: 4,
                      md: 3
                    }}>
                    <TextField
                      fullWidth
                      label="Community Engagement"
                      type="number"
                      inputProps={{ step: 1, min: 0, max: 100 }}
                      value={editFormData.community_engagement_score}
                      onChange={(e) => setEditFormData({ ...editFormData, community_engagement_score: e.target.value })}
                      disabled={saving}
                      size="small"
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 6,
                      sm: 4,
                      md: 3
                    }}>
                    <TextField
                      fullWidth
                      label="Leadership Strength"
                      type="number"
                      inputProps={{ step: 1, min: 0, max: 100 }}
                      value={editFormData.leadership_strength_score}
                      onChange={(e) => setEditFormData({ ...editFormData, leadership_strength_score: e.target.value })}
                      disabled={saving}
                      size="small"
                    />
                  </Grid>

                  {/* Faana Growth Metrics */}
                  <Grid size={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>Growth Metrics</Typography>
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Growth Trajectory</InputLabel>
                      <Select
                        value={editFormData.growth_trajectory || ''}
                        label="Growth Trajectory"
                        onChange={(e) => setEditFormData({ ...editFormData, growth_trajectory: e.target.value })}
                        disabled={saving}
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="Beginning">Beginning</MenuItem>
                        <MenuItem value="Developing">Developing</MenuItem>
                        <MenuItem value="Advanced">Advanced</MenuItem>
                        <MenuItem value="Master">Master</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Growth Velocity</InputLabel>
                      <Select
                        value={editFormData.growth_velocity || ''}
                        label="Growth Velocity"
                        onChange={(e) => setEditFormData({ ...editFormData, growth_velocity: e.target.value })}
                        disabled={saving}
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="Slow">Slow</MenuItem>
                        <MenuItem value="Moderate">Moderate</MenuItem>
                        <MenuItem value="Rapid">Rapid</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <TextField
                      fullWidth
                      label="Assessment Date"
                      type="date"
                      value={editFormData.assessment_date || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, assessment_date: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      disabled={saving}
                      size="small"
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <TextField
                      fullWidth
                      label="Coach Name"
                      value={editFormData.coach_name || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, coach_name: e.target.value })}
                      disabled={saving}
                      size="small"
                    />
                  </Grid>

                  {/* Faana Risk Assessment */}
                  <Grid size={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>Risk Assessment</Typography>
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <TextField
                      fullWidth
                      label="Growth Risk Index"
                      type="number"
                      inputProps={{ step: 0.1, min: 0, max: 5 }}
                      value={editFormData.growth_risk_index}
                      onChange={(e) => setEditFormData({ ...editFormData, growth_risk_index: e.target.value })}
                      disabled={saving}
                      size="small"
                      helperText="0-5 scale (lower is better)"
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <TextField
                      fullWidth
                      label="Risk Factors"
                      value={editFormData.risk_factors || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, risk_factors: e.target.value })}
                      disabled={saving}
                      size="small"
                      helperText="Comma-separated (e.g., tech_disruption, budget_constraints)"
                    />
                  </Grid>

                  {/* Notes */}
                  <Grid size={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>Notes</Typography>
                  </Grid>

                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Notes"
                      value={editFormData.notes || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                      multiline
                      rows={4}
                      disabled={saving}
                    />
                  </Grid>
                </>
              )}

              {editFormData.type === 'PROJECT' && (
                <>
                  <Grid size={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>Project Details</Typography>
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      type="date"
                      value={editFormData.startDate || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, startDate: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      disabled={saving}
                    />
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <TextField
                      fullWidth
                      label="End Date"
                      type="date"
                      value={editFormData.endDate || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, endDate: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      disabled={saving}
                    />
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <TextField
                      fullWidth
                      label="Project Lead"
                      value={editFormData.projectLead || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, projectLead: e.target.value })}
                      disabled={saving}
                    />
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <TextField
                      fullWidth
                      label="Budget"
                      value={editFormData.budget || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, budget: e.target.value })}
                      disabled={saving}
                    />
                  </Grid>

                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Objectives"
                      value={editFormData.objectives || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, objectives: e.target.value })}
                      multiline
                      rows={3}
                      disabled={saving}
                    />
                  </Grid>
                </>
              )}

              {editFormData.type === 'EVENT' && (
                <>
                  <Grid size={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>Event Details</Typography>
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <TextField
                      fullWidth
                      label="Event Date"
                      type="date"
                      value={editFormData.eventDate || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, eventDate: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      disabled={saving}
                    />
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <TextField
                      fullWidth
                      label="Event Time"
                      type="time"
                      value={editFormData.eventTime || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, eventTime: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      disabled={saving}
                    />
                  </Grid>

                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Location"
                      value={editFormData.location || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
                      disabled={saving}
                    />
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <TextField
                      fullWidth
                      label="Event Type"
                      value={editFormData.eventType || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, eventType: e.target.value })}
                      disabled={saving}
                    />
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <TextField
                      fullWidth
                      label="Capacity"
                      type="number"
                      value={editFormData.capacity || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, capacity: e.target.value })}
                      disabled={saving}
                    />
                  </Grid>

                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Registration URL"
                      value={editFormData.registrationUrl || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, registrationUrl: e.target.value })}
                      disabled={saving}
                    />
                  </Grid>

                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Agenda"
                      value={editFormData.agenda || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, agenda: e.target.value })}
                      multiline
                      rows={3}
                      disabled={saving}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
            onClick={handleSaveEntity}
            disabled={saving || !editFormData?.name}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Delete color="error" />
              <Typography variant="h6">Delete Entity</Typography>
            </Box>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setDeleteDialogOpen(false)}
              aria-label="close"
              disabled={deleting}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The entity will be permanently deleted.
          </Alert>

          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError(null)}>
              {deleteError}
            </Alert>
          )}

          {selectedEntity && (
            <>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to delete the entity <strong>"{selectedEntity.name}"</strong>?
              </Typography>

              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" component="div">
                  Entity Type: <Chip label={selectedEntity.type} size="small" color={getTypeColor(selectedEntity.type)} />
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Entity ID: {selectedEntity.id || selectedEntity.entityId}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 3, mb: 1 }}>
                To confirm deletion, please type <strong>{selectedEntity.name}</strong> below:
              </Typography>

              <TextField
                fullWidth
                variant="outlined"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={`Type "${selectedEntity.name}" to confirm`}
                autoFocus
                disabled={deleting}
                error={deleteError && deleteConfirmText !== selectedEntity.name}
                helperText={deleteError && deleteConfirmText !== selectedEntity.name ? 'Name must match exactly' : ''}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && deleteConfirmText === selectedEntity.name) {
                    confirmDelete();
                  }
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={deleting ? <CircularProgress size={20} /> : <Delete />}
            onClick={confirmDelete}
            disabled={deleting || !selectedEntity || deleteConfirmText !== selectedEntity.name}
          >
            {deleting ? 'Deleting...' : 'Delete Entity'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ExistingEntities;